import { useState, useEffect, useCallback } from 'react';
import {
  Divider,
  Box,
  Button,
  CircularProgress,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PropTypes from 'prop-types';

import { fNumber } from '../../utils/formatNumber';
import { NAME_REGEX, DESCRIPTION_REGEX, PHONE_REGEX, EMAIL_REGEX } from '../../utils/constants';
import axiosInstance from '../../api';
import SERVER_API_URL from '../../config';
import {
  REQUIRED_FIELD,
  NAME_EXCEPTION,
  DESCRIPTION_EXCEPTION,
  PHONE_EXCEPTION,
  EMAIL_EXCEPTION,
  INTERNAL_SERVER_ERROR,
} from '../../utils/exception';
import RegisteredBy from '../common/RegisteredBy';
import UpdaterList from '../common/UpdaterList';
import DateInfo from '../common/DateInfo';
import EditAvatar from './EditAvatar';
import StyledCardNoHeight from '../../components/card/StyledCardNoHeight';
import RegistererButton from '../common/RegistererButton';
import UpdaterButton from '../common/UpdaterButton';
import DownloadPDFButton from './DownloadPDFButton';

export default function DistrictUpdateForm({ districtToBeUpdated, setDistrictToBeUpdated, user }) {
  const [disabled, setDisabled] = useState(false);
  const [firstDisabled, setFirstDisabled] = useState(true);
  const [openRegisterer, setOpenRegisterer] = useState(null);
  const [openUpdater, setOpenUpdater] = useState(null);
  const [registeredBy, setRegisteredBy] = useState(null);
  const [updaterList, setUpdaterList] = useState([]);

  const handleRegistererMenu = (event) => {
    setOpenRegisterer(event.currentTarget);
  };

  const handleUpdaterMenu = (event) => {
    setOpenUpdater(event.currentTarget);
  };

  const handleCloseMenu = async () => {
    setOpenRegisterer(null);
    setOpenUpdater(null);
  };

  const getRegisteredBy = useCallback(async () => {
    try {
      const response = await axiosInstance.post(
        `${SERVER_API_URL}/district/findRegisteredBy/${districtToBeUpdated.id}`
      );
      if (response.status === 200) {
        setRegisteredBy(response.data);
      }
    } catch (error) {
      // console.log(error)
    }
  }, [districtToBeUpdated]);

  const getUpdaterList = useCallback(async () => {
    try {
      const response = await axiosInstance.post(`${SERVER_API_URL}/district/findUpdaterList/${districtToBeUpdated.id}`);
      if (response.status === 200) {
        setUpdaterList(response.data);
      }
    } catch (error) {
      // console.log(error)
    }
  }, [districtToBeUpdated]);

  useEffect(() => {
    getRegisteredBy();
    getUpdaterList();
  }, [getRegisteredBy, getUpdaterList]);

  const handleRefreshUserData = async () => {
    try {
      const response = await axiosInstance.get(`${SERVER_API_URL}/district/findOneById/${districtToBeUpdated.id}`);
      if (response.status === 200) {
        setDistrictToBeUpdated(response.data);
      }
    } catch (error) {
      // console.log(error)
    }
  };

  const handleUpdateDistrict = async (event) => {
    event.preventDefault();
    setFormErrorSeverity('error');
    setDisabled(true);
    setFirstDisabled(true);

    if (
      !formErrorOnDistrictName &&
      !formErrorOnDescription &&
      !formErrorOnPhone &&
      !formErrorOnEmail &&
      !formErrorOnHistory
    ) {
      const { districtName, description, phone, email, history } = event.target.elements;

      try {
        const response = await axiosInstance.put(
          `${SERVER_API_URL}/district/updateOne/${districtToBeUpdated.id}/${user && user.id}`,
          {
            districtName: districtName.value,
            description: description.value,
            phone: phone.value,
            email: email.value,
            history: history.value,
          }
        );

        if (response.status === 200) {
          handleRefreshUserData();
          getUpdaterList();
          setFormError('ወረዳ በተሳካ ሁኔታ ተስተካክሏል።');
          setFormErrorSeverity('success');
        }
      } catch (e) {
        if (e.response.status === 406) {
          setFormError(e.response.data.message);
        } else if (e.response.status === 400) {
          setFormError(e.response.data.message);
        } else if (e.response.status === 500) {
          setFormError(INTERNAL_SERVER_ERROR);
        }
      }
    }
    setDisabled(false);
  };

  const [history, setHistory] = useState(districtToBeUpdated.history);
  const [districtName, setDistrictName] = useState(districtToBeUpdated.name);
  const [description, setDescription] = useState(districtToBeUpdated.description);
  const [phone, setPhone] = useState(districtToBeUpdated.phone.slice(4, 13));
  const [email, setEmail] = useState(districtToBeUpdated.email);

  const [formError, setFormError] = useState(null);
  const [formErrorSeverity, setFormErrorSeverity] = useState('error');

  const [districtNameClicked, setDistrictNameClicked] = useState(false);
  const [formErrorOnDistrictName, setFormErrorOnDistrictName] = useState(false);
  const [helperTextOnDistrictName, setHelperTextOnDistrictName] = useState(null);

  const [descriptionClicked, setDescriptionClicked] = useState(false);
  const [formErrorOnDescription, setFormErrorOnDescription] = useState(false);
  const [helperTextOnDescription, setHelperTextOnDescription] = useState(null);

  const [phoneClicked, setPhoneClicked] = useState(false);
  const [formErrorOnPhone, setFormErrorOnPhone] = useState(false);
  const [helperTextOnPhone, setHelperTextOnPhone] = useState(null);

  const [emailClicked, setEmailClicked] = useState(false);
  const [formErrorOnEmail, setFormErrorOnEmail] = useState(false);
  const [helperTextOnEmail, setHelperTextOnEmail] = useState(null);

  const [historyClicked, setHistoryClicked] = useState(false);
  const [formErrorOnHistory, setFormErrorOnHistory] = useState(false);
  const [helperTextOnHistory, setHelperTextOnHistory] = useState(null);

  const handleFormValueBlur = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');

    const { name, value } = e.target;
    switch (name) {
      case 'districtName':
        if (value.length === 0) {
          setDistrictNameClicked(false);
          setFormErrorOnDistrictName(districtNameClicked && value.length === 0);
          setHelperTextOnDistrictName(REQUIRED_FIELD);
        } else if (!NAME_REGEX.test(value)) {
          setFormErrorOnDistrictName(true);
          setHelperTextOnDistrictName(NAME_EXCEPTION);
        } else {
          setHelperTextOnDistrictName(null);
          setFormErrorOnDistrictName(false);
        }
        break;
      case 'description':
        if (value.length === 0) {
          setDescriptionClicked(false);
          setFormErrorOnDescription(descriptionClicked && value.length === 0);
          setHelperTextOnDescription(REQUIRED_FIELD);
        } else if (!DESCRIPTION_REGEX.test(value)) {
          setFormErrorOnDescription(true);
          setHelperTextOnDescription(DESCRIPTION_EXCEPTION);
        } else {
          setHelperTextOnDescription(null);
          setFormErrorOnDescription(false);
        }
        break;
      case 'phone':
        if (value.length === 0) {
          setPhoneClicked(false);
          setFormErrorOnPhone(phoneClicked && value.length === 0);
          setHelperTextOnPhone(REQUIRED_FIELD);
        } else if (!PHONE_REGEX.test(value)) {
          setFormErrorOnPhone(true);
          setHelperTextOnPhone(PHONE_EXCEPTION);
        } else {
          setHelperTextOnPhone(null);
          setFormErrorOnPhone(false);
        }
        break;
      case 'email':
        if (value.length === 0) {
          setEmailClicked(false);
          setFormErrorOnEmail(emailClicked && value.length === 0);
          setHelperTextOnEmail(REQUIRED_FIELD);
        } else if (!EMAIL_REGEX.test(value)) {
          setFormErrorOnEmail(true);
          setHelperTextOnEmail(EMAIL_EXCEPTION);
        } else {
          setHelperTextOnEmail(null);
          setFormErrorOnEmail(false);
        }
        break;
      case 'history':
        if (value.length === 0) {
          setHistoryClicked(false);
          setFormErrorOnHistory(historyClicked && value.length === 0);
          setHelperTextOnHistory(REQUIRED_FIELD);
        } else {
          setHelperTextOnHistory(null);
          setFormErrorOnHistory(false);
        }
        break;
      default:
        break;
    }
  };

  const handleFormValueChange = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');
    setFirstDisabled(false);

    const { name, value } = e.target;
    switch (name) {
      case 'districtName':
        setDistrictName(value);
        setFormErrorOnDistrictName(false);
        setHelperTextOnDistrictName(null);
        break;
      case 'description':
        setDescription(value);
        setFormErrorOnDescription(false);
        setHelperTextOnDescription(null);
        break;
      case 'phone':
        setPhone(value);
        setFormErrorOnPhone(false);
        setHelperTextOnPhone(null);
        break;
      case 'email':
        setEmail(value);
        setFormErrorOnEmail(false);
        setHelperTextOnEmail(null);
        break;
      case 'history':
        setHistory(value);
        setFormErrorOnHistory(false);
        setHelperTextOnHistory(null);
        break;
      default:
        break;
    }
  };

  const handleClearForm = () => {
    setHistory(districtToBeUpdated.history);
    setDistrictName(districtToBeUpdated.name);
    setDescription(districtToBeUpdated.description);
    setPhone(districtToBeUpdated.phone.slice(4, 13));
    setEmail(districtToBeUpdated.email);

    setFormError(null);
    setFormErrorSeverity('error');
    setFirstDisabled(true);

    setDistrictNameClicked(false);
    setFormErrorOnDistrictName(false);
    setHelperTextOnDistrictName(null);

    setDescriptionClicked(false);
    setFormErrorOnDescription(false);
    setHelperTextOnDescription(null);

    setPhoneClicked(false);
    setFormErrorOnPhone(false);
    setHelperTextOnPhone(null);

    setEmailClicked(false);
    setFormErrorOnEmail(false);
    setHelperTextOnEmail(null);

    setHistoryClicked(false);
    setFormErrorOnHistory(false);
    setHelperTextOnHistory(null);
  };

  return (
    <StyledCardNoHeight sx={{ padding: 5 }} component="form" autoComplete="off" onSubmit={handleUpdateDistrict}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <EditAvatar districtToBeUpdated={districtToBeUpdated} />
        </Grid>{' '}
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ ml: 2 }}>
              <Typography variant="h5" sx={{ color: 'text.secondary' }}>
                {districtToBeUpdated.name} ወረዳ
              </Typography>
              <Typography sx={{ color: 'darkgray' }}>
                {fNumber(districtToBeUpdated && districtToBeUpdated.churchCount)} : አብያተ ክርስቲያናት,
                <br />
                {fNumber(districtToBeUpdated && districtToBeUpdated.usersCount)} : ተከታዮች
              </Typography>
            </Box>
            <DownloadPDFButton district={districtToBeUpdated} user={user} />
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            {districtToBeUpdated && (
              <DateInfo createdAt={districtToBeUpdated.createdAt} updatedAt={districtToBeUpdated.updatedAt} />
            )}
            <Box sx={{ display: 'flex', justifyContent: 'right', gap: 1 }}>
              {updaterList && <UpdaterButton updaterList={updaterList} handleUpdaterMenu={handleUpdaterMenu} />} :{' '}
              {registeredBy && (
                <RegistererButton registeredBy={registeredBy} handleRegistererMenu={handleRegistererMenu} />
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Divider sx={{ borderStyle: 'dashed' }} />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} sm={12}>
          {formError && (
            <Alert sx={{ width: '100%' }} severity={formErrorSeverity}>
              {formError}
            </Alert>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12}>
              <TextField
                required
                error={formErrorOnDistrictName}
                id="districtName"
                name="districtName"
                label="የወረዳ ስም"
                value={districtName}
                fullWidth
                // variant="standard"
                onChange={handleFormValueChange}
                helperText={formErrorOnDistrictName ? helperTextOnDistrictName : null}
                onFocus={() => setDistrictNameClicked(true)}
                onBlur={handleFormValueBlur}
              />
            </Grid>{' '}
            <Grid item xs={12} sm={12}>
              <TextField
                required
                error={formErrorOnPhone}
                id="phone"
                name="phone"
                label="የወረዳ ስልክ ቁጥር"
                value={phone}
                fullWidth
                // variant="standard"
                InputProps={{
                  startAdornment: <InputAdornment position="start">+251</InputAdornment>,
                }}
                onChange={handleFormValueChange}
                helperText={formErrorOnPhone ? helperTextOnPhone : null}
                onFocus={() => setPhoneClicked(true)}
                onBlur={handleFormValueBlur}
              />
            </Grid>{' '}
            <Grid item xs={12} sm={12}>
              <TextField
                required
                error={formErrorOnEmail}
                id="email"
                name="email"
                placeholder="example@gmail.com"
                label="የወረዳ ኢሜይል"
                value={email}
                fullWidth
                // variant="standard"
                onChange={handleFormValueChange}
                helperText={formErrorOnEmail ? helperTextOnEmail : null}
                onFocus={() => setEmailClicked(true)}
                onBlur={handleFormValueBlur}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            error={formErrorOnDescription}
            id="description"
            name="description"
            label="የወረዳ መግለጫ"
            value={description}
            fullWidth
            multiline
            rows={7}
            onChange={handleFormValueChange}
            helperText={formErrorOnDescription ? helperTextOnDescription : null}
            onFocus={() => setDescriptionClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            error={formErrorOnHistory}
            id="history"
            name="history"
            label="የወረዳ አጭር ታሪክ"
            value={history}
            fullWidth
            multiline
            rows={7}
            onChange={handleFormValueChange}
            helperText={formErrorOnHistory ? helperTextOnHistory : null}
            onFocus={() => setHistoryClicked(true)}
            onBlur={handleFormValueBlur}
          />
        </Grid>
        <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'right', m: 2 }}>
          <Button color="error" type="reset" onClick={handleClearForm} sx={{ minWidth: { xs: 80, sm: 150 } }}>
            <RefreshIcon />
            ቅጽ ዳግም አስጀምር
          </Button>

          <Button
            variant="contained"
            type="submit"
            sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: 80, sm: 150 } }}
            disabled={firstDisabled}
          >
            {disabled && <CircularProgress style={{ width: '20px', height: '20px' }} />}
            ቅጽ አስገባ
          </Button>
        </Grid>
      </Grid>
      {registeredBy && (
        <RegisteredBy openRegister={openRegisterer} registeredBy={registeredBy} handleCloseMenu={handleCloseMenu} />
      )}
      {updaterList && (
        <UpdaterList openUpdater={openUpdater} updaterList={updaterList} handleCloseMenu={handleCloseMenu} />
      )}
    </StyledCardNoHeight>
  );
}

// Add PropTypes validation for all props
DistrictUpdateForm.propTypes = {
  districtToBeUpdated: PropTypes.object.isRequired,
  setDistrictToBeUpdated: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
};
